import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { safeHref } from './taskMarkdown';
import * as M from './TaskModal.styled';

interface ITaskDescriptionProps {
  value?: string | null;
}

/**
 * Описание задачи разметкой (ТЗ 0066).
 *
 * Раньше описание было plain-text: вставленная ссылка оставалась мёртвым
 * текстом, который выделяли и копировали руками. `remark-gfm` делает голый
 * URL ссылкой сам (GFM autolink), поэтому специально «линкифицировать» ничего
 * не нужно.
 *
 * 🔴 Почему это безопасно БЕЗ санитайзера: react-markdown v6 без `rehype-raw`
 * не выпускает сырой HTML вовсе, а `javascript:` в адресе гасит встроенный
 * `transformLinkUri`. Опасного вывода не существует — это сильнее, чем
 * фильтрация опасного вывода. Ровно тот же довод записан в `MessageBody`.
 */
export const TaskDescription = memo(({ value }: ITaskDescriptionProps) => {
  if (!value || !value.trim()) return null;

  return (
    <M.Markdown>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        linkTarget="_blank"
        transformLinkUri={safeHref}
        components={{
          // `rel` руками: современные браузеры подразумевают `noopener` для
          // `target=_blank`, но не все, а цена промаха — доступ чужой вкладки
          // к `window.opener`.
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          // 🔴 Картинки по ссылке НЕ рисуем. `![](http://чужой-хост/x.png)` в
          // описании — это трекинг-пиксель: он отдаёт IP и время просмотра
          // каждого, кто откроет задачу, владельцу чужого хоста. Показываем
          // адрес ссылкой — содержимое остаётся доступным, слежка нет.
          // Свои файлы живут во вложениях, а не в тексте: их ссылка
          // подписанная и живёт пять минут, в разметке ей не место.
          img: ({ src, alt }) => (
            <a href={src} target="_blank" rel="noopener noreferrer">
              {alt || src}
            </a>
          ),
        }}
      >
        {value}
      </ReactMarkdown>
    </M.Markdown>
  );
});

TaskDescription.displayName = 'TaskDescription';
